/**
 * Listens for deep-link navigation requests (from push-notification click handlers
 * and native custom-scheme / Universal Link opens) and performs them via React Router
 * instead of a hard page reload. Must be rendered inside the <BrowserRouter>.
 * See `src/lib/deepLinkNavigation.ts` for the bridge.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  DEEP_LINK_EVENT,
  consumePendingDeepLink,
  isInternalDeepLink,
  setPendingDeepLink,
} from '../lib/deepLinkNavigation';
import { nativeOpenUrlToAppPath } from '../lib/nativeAppScheme';
import { stashGbpOauthResume } from '../lib/gbpOauthResume';

/** Persist GBP OAuth tokens from a deep-link URL into WebView sessionStorage. */
function stashOauthFromAppPath(appPath: string): void {
  try {
    const qIndex = appPath.indexOf('?');
    if (qIndex < 0) return;
    const params = new URLSearchParams(appPath.slice(qIndex + 1));
    const pendingDataToken =
      params.get('pendingDataToken') || params.get('pending_data_token') || '';
    if (!pendingDataToken) return;
    const profileId = params.get('profileId') || params.get('profile_id') || '';
    const connectToken = params.get('connect_token') || params.get('connectToken');
    let businessId = '';
    try {
      businessId = sessionStorage.getItem('gbp_connect_business_id') || '';
    } catch {
      /* ignore */
    }
    stashGbpOauthResume({
      businessId,
      profileId,
      pendingDataToken,
      connectToken: connectToken || null,
    });
    try {
      sessionStorage.setItem('gbp_open_settings', '1');
      if (profileId) sessionStorage.setItem('gbp_connect_profile_id', profileId);
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
}

/**
 * iOS keeps the last custom-scheme URL for the life of the process.
 * Reading it again on every in-app navigation replays a finished Google
 * return (and the "couldn't connect" modal) until the app is force-quit.
 */
let nativeLaunchUrlConsumed = false;

export function DeepLinkNavigator() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const go = (url: string) => {
      if (!isInternalDeepLink(url)) return;
      // Siri "Talk to Nexus" (and any assistant/voice deep link): start the
      // AI voice session instead of navigating — there is no such route.
      if (url.startsWith('/assistant/voice')) {
        consumePendingDeepLink(); // never replay a voice start on a later boot
        window.dispatchEvent(new CustomEvent('assistant:startVoice'));
        return;
      }
      stashOauthFromAppPath(url);
      // Avoid a redundant navigation if we're already exactly there.
      const current = `${window.location.pathname}${window.location.search}`;
      if (current === url) return;
      // Prefer a clean integrations URL once tokens are stashed in WebView storage.
      const hasOauth =
        url.includes('pendingDataToken=') ||
        url.includes('pending_data_token=') ||
        url.includes('gbp_oauth=1');
      if (hasOauth && (url.startsWith('/app-store') || url.startsWith('/integrations'))) {
        navigateRef.current('/app-store?tab=integrations&gbp_returning=1', { replace: true });
        return;
      }
      navigateRef.current(url);
    };

    const handler = (event: Event) => {
      const url = (event as CustomEvent<{ url?: string }>).detail?.url;
      // Clear any stash so it isn't replayed again later.
      consumePendingDeepLink();
      if (url) go(url);
    };

    window.addEventListener(DEEP_LINK_EVENT, handler);

    // Cold-start replay: a push tapped while the app was closed may have stashed a
    // target before this navigator mounted. Defer so the route tree is ready.
    const pending = consumePendingDeepLink();
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (pending) {
      timer = setTimeout(() => go(pending), 0);
    }

    let removeAppUrlOpen: (() => void) | undefined;
    let cancelled = false;

    if (Capacitor.isNativePlatform()) {
      void (async () => {
        try {
          const { App } = await import('@capacitor/app');
          const { Browser } = await import('@capacitor/browser');

          const handleNativeUrl = (url: string) => {
            if (cancelled) return;
            const path = nativeOpenUrlToAppPath(url);
            if (!path) return;
            setPendingDeepLink(path);
            go(path);
            // Close SFSafariViewController / Chrome Custom Tab if OAuth used Browser.open
            void Browser.close().catch(() => undefined);
          };

          const appUrlSub = await App.addListener('appUrlOpen', ({ url }) => {
            handleNativeUrl(url);
          });
          if (cancelled) {
            void appUrlSub.remove();
            return;
          }
          removeAppUrlOpen = () => {
            void appUrlSub.remove();
          };

          // Cold start only. A later in-app navigation must not read this again:
          // Capacitor keeps lastURL until the process dies.
          if (!nativeLaunchUrlConsumed) {
            nativeLaunchUrlConsumed = true;
            const launch = await App.getLaunchUrl();
            if (!cancelled && launch?.url) handleNativeUrl(launch.url);
          }
        } catch (err) {
          console.warn('[DeepLinkNavigator] native URL listener unavailable', err);
        }
      })();
    }

    return () => {
      cancelled = true;
      window.removeEventListener(DEEP_LINK_EVENT, handler);
      if (timer) clearTimeout(timer);
      removeAppUrlOpen?.();
    };
  }, []);

  return null;
}
